import type { DivisionId } from './division.js';

/** 'registration' : coquille créée, roster/grille pas encore générés, les
 * joueurs peuvent inscrire leur pilote. 'active' : verrouillée, en cours.
 * 'completed' : campagne terminée. */
export type SeasonStatus = 'registration' | 'active' | 'completed';
export type MeetingStatus = 'scheduled' | 'in_progress' | 'completed';
export type MeetingPhase = 'qual' | 'semi' | 'final' | 'completed';
export type QualMancheNum = 1 | 2 | 3 | 4;
export type QualRaceNum = 1 | 2 | 3 | 4;
export type QualSessionType = `qual_${QualMancheNum}_${QualRaceNum}`;
export type QualTiebreakSessionType = `qual_tb_${number}`;
export type MeetingSessionType =
  QualSessionType | QualTiebreakSessionType | 'semi_1' | 'semi_2' | 'final';
export type MeetingSessionStatus = 'pending' | 'completed';
export type SessionResolution = 'simulated' | 'played';

/** @deprecated Utiliser DEFAULT_SEASON_MEETING_COUNT ou le choix à la création. */
export const MEETINGS_PER_SEASON = 6;

/** Nombre de meetings sélectionnables à la création d'une campagne (cf. règles design option B). */
export const SEASON_MEETING_COUNT_OPTIONS = [4, 6] as const;
export type SeasonMeetingCount = (typeof SEASON_MEETING_COUNT_OPTIONS)[number];
export const DEFAULT_SEASON_MEETING_COUNT: SeasonMeetingCount = 6;

export function normalizeSeasonMeetingCount(raw: unknown): SeasonMeetingCount {
  const n = Number(raw);
  if ((SEASON_MEETING_COUNT_OPTIONS as readonly number[]).includes(n)) {
    return n as SeasonMeetingCount;
  }
  return DEFAULT_SEASON_MEETING_COUNT;
}

export interface StartSeasonBody {
  meetingCount?: number;
  divisionId?: number;
  division?: number;
  /** Ordre des circuits pour chaque meeting (longueur = meetingCount). */
  circuitCalendar?: number[];
}
/** Nombre de manches de qualification par meeting. */
export const QUAL_MANCHES = 4;
/** Courses par manche qualif (4 × 6 pilotes = 24 pilotes / manche). */
export const QUAL_RACES_PER_MANCHE = 4;
/** @deprecated Alias de {@link QUAL_MANCHES}. */
export const QUAL_HEATS = QUAL_MANCHES;
export const QUAL_SESSION_COUNT = QUAL_MANCHES * QUAL_RACES_PER_MANCHE;
/** Pilotes par course de qualification. */
export const QUAL_DRIVERS_PER_HEAT = 6;
/** Pilotes par demi-finale ou finale. */
export const DRIVERS_PER_SESSION = 6;
export const QUAL_LAPS = 4;
/** Tours pour une course de barrage qualif (ex æquo de points qualif). */
export const QUAL_TIE_BREAK_LAPS = 2;
export const SEMI_FINAL_LAPS = 6;
export const FINAL_LAPS = 6;
export const QUAL_ADVANCE_COUNT = 12;
export const SEMI_ADVANCE_PER_GROUP = 3;

export interface GridSlot {
  playerIndex: number;
  driverId: number;
  carId: number;
  driverName: string;
  carName: string;
  nationality?: string;
}

export interface DriverClaim {
  driverId: number;
  claimerId: string;
  ready: boolean;
  displayName?: string;
}

export interface SessionResultRow {
  driverId: number;
  position: number;
  diceRolls: number;
  /**
   * Total de duels (legacy). Préférer `overtakesSucceeded` / `overtakesCountered`.
   * Conservé pour lecture JSON historique.
   */
  overtakeDuels?: number;
  /** Dépassements réussis (attaquant). */
  overtakesSucceeded?: number;
  /** Dépassements contrés (défenseur). */
  overtakesCountered?: number;
}

export interface MeetingSessionSummary {
  id: number;
  meetingId: number;
  sessionType: MeetingSessionType;
  status: MeetingSessionStatus;
  numLaps: number;
  numParticipants: number;
  resolution: SessionResolution | null;
  raceHistoryId?: number | null;
  sequence: number;
  label: string;
}

export interface MeetingSummary {
  id: number;
  seasonId: number;
  meetingNumber: number;
  circuitId: number;
  circuitName?: string;
  status: MeetingStatus;
  phase: MeetingPhase;
  sessions: MeetingSessionSummary[];
}

export interface SeasonSummary {
  id: number;
  /** Absent pour une vue globale multi-divisions */
  division?: DivisionId;
  status: SeasonStatus;
  meetingCount: number;
  currentMeetingIndex: number;
  createdAt?: string;
  /** Nombre de pilotes inscrits — uniquement peuplé quand status === 'registration'. */
  registrationCount?: number;
}

/** Réponse de `/api/seasons/registration/join` et `/status` : où en est le
 * pilote du joueur connecté (division choisie / courante). */
export interface SeasonRegistrationStatusView {
  /** Division d'inscription (si déjà inscrit) ou division courante du pilote. */
  division: DivisionId | null;
  seasonId: number | null;
  registered: boolean;
  count: number;
  capacity: number;
}

/** Saison globale : D1–D4 en parallèle (une reprise, un calendrier commun). */
export interface GlobalActiveSeasonView {
  meetingCount: number;
  /** Meeting le plus avancé parmi les divisions actives */
  currentMeetingIndex: number;
  status: SeasonStatus;
  divisions: Partial<Record<DivisionId, ActiveSeasonView>>;
}

export interface SeasonDriverPointsRow {
  driverId: number;
  driverName: string;
  /** Points championnat (demi + finale). Rang : champ → qualif → course. */
  points: number;
  /** Total points de qualification (heats) sur la saison. */
  qualPoints?: number;
  /** Solde monnaie « points de course ». */
  coursePoints?: number;
  rank: number;
}

export interface ActiveSeasonView {
  season: SeasonSummary;
  currentMeeting: MeetingSummary | null;
  nextSession: MeetingSessionDetail | null;
  standings: SeasonDriverPointsRow[];
}

export interface MeetingSessionDetail {
  id: number;
  meetingId: number;
  seasonId: number;
  division: DivisionId;
  sessionType: MeetingSessionType;
  status: MeetingSessionStatus;
  numLaps: number;
  circuitId: number;
  circuitName?: string;
  grid: GridSlot[];
  claims: DriverClaim[];
  resolution: SessionResolution | null;
  label: string;
  /** Vrai si le plateau (tour de chauffe) doit encore se jouer avant cette
   * session — voir `isBoardWarmupDue`. */
  boardWarmupPending: boolean;
}

export interface LobbyDriverView {
  slot: GridSlot;
  taken: boolean;
  takenByClaimerId: string | null;
  ready: boolean;
}

export interface LobbyState {
  sessionId: number;
  status: MeetingSessionStatus;
  drivers: LobbyDriverView[];
  claims: DriverClaim[];
  canStart: boolean;
  isHost?: boolean;
  waitingLabel: string;
}

export interface PlayConfig {
  sessionId: number;
  meetingId: number;
  seasonId: number;
  division: DivisionId;
  circuitId: number;
  numLaps: number;
  numParticipants: number;
  grid: GridSlot[];
  humanDriverId: number;
  humanPlayerIndex: number;
  sessionType: MeetingSessionType;
  label: string;
  /** Ordre de jeu au premier lancer (qualif : tirage ; demi : classement qualif ; finale : demis). */
  initialTurnOrder: number[];
}

export interface CompleteSessionBody {
  results: SessionResultRow[];
  raceHistoryId?: number;
  humanDriverId?: number;
}

export interface SessionResultDisplayRow extends SessionResultRow {
  driverName: string;
  carName: string;
  nationality?: string;
}

export interface SimulateSessionResponse {
  season: GlobalActiveSeasonView;
  completedSession: CompletedSessionInfo;
  qualMancheRecap?: QualMancheRecap | null;
  qualFinalRecap?: QualFinalRecap | null;
}

export interface CompletedSessionInfo {
  sessionId: number;
  label: string;
  division: DivisionId;
  sessionType: MeetingSessionType;
  results: SessionResultDisplayRow[];
  /** Replay persisté (simulate / partie jouée) — ouvrir `#archives/:id`. */
  raceHistoryId?: number | null;
}

export interface QualMancheRecapRow {
  driverId: number;
  driverName: string;
  qualPoints: number;
  diceRolls: number;
  championshipBonus: number;
}

export interface QualMancheRecap {
  manche: number;
  division: DivisionId;
  fewestDiceRolls: number;
  bonusDriverIds: number[];
  rows: QualMancheRecapRow[];
}

export interface QualFinalRecapRow {
  driverId: number;
  driverName: string;
  qualPoints: number;
  totalDiceRolls: number;
  /** Bonus +1 par manche (minimum de lancers). */
  mancheBonuses: number;
  /** Points 25→2 selon le classement qualif final. */
  rankChampionshipPoints: number;
  /** Total des points championnat gagnés pendant les qualifs. */
  totalChampionshipPoints: number;
}

export interface QualFinalRecap {
  division: DivisionId;
  hadTiebreaks: boolean;
  rows: QualFinalRecapRow[];
}

export interface QualLiveStandingRow {
  rank: number;
  driverId: number;
  driverName: string;
  qualPoints: number;
  totalDiceRolls: number;
  racesCompleted: number;
  raceWins: number;
}

export interface QualLiveStandingsView {
  meetingId: number;
  meetingNumber: number;
  division: DivisionId;
  completedQualSessions: number;
  totalQualSessions: number;
  rows: QualLiveStandingRow[];
}

export interface CompleteSessionResponse {
  season: GlobalActiveSeasonView | ActiveSeasonView;
  completedSession: CompletedSessionInfo;
  qualMancheRecap?: QualMancheRecap | null;
  qualFinalRecap?: QualFinalRecap | null;
}

/** Campagne multi-divisions (4 saisons démarrées ensemble). */
export interface SeasonCampaignSummary {
  campaignKey: string;
  startedAt: string;
  status: SeasonStatus;
  meetingCount: number;
  currentMeetingIndex: number;
  seasonIds: Partial<Record<DivisionId, number>>;
  divisions: DivisionId[];
}

export interface SeasonCampaignDivisionDetail {
  division: DivisionId;
  seasonId: number;
  standings: SeasonDriverPointsRow[];
  meetings: MeetingSummary[];
}

export interface SeasonCampaignDetail {
  campaignKey: string;
  startedAt: string;
  status: SeasonStatus;
  meetingCount: number;
  currentMeetingIndex: number;
  divisions: SeasonCampaignDivisionDetail[];
}

export interface ClaimDriverBody {
  driverId: number;
  claimerId: string;
  displayName?: string;
  /** Marque directement le claim prêt (évite un second aller-retour claim→ready,
   * qui pouvait se chevaucher côté socket et effacer le claim — voir `mergeDriverClaim`). */
  ready?: boolean;
}

export interface ReleaseClaimBody {
  claimerId: string;
}

export function qualSessionType(manche: number, race: number): QualSessionType {
  return `qual_${manche}_${race}` as QualSessionType;
}

export const ALL_QUAL_SESSION_TYPES: QualSessionType[] = (() => {
  const out: QualSessionType[] = [];
  for (let m = 1; m <= QUAL_MANCHES; m++) {
    for (let r = 1; r <= QUAL_RACES_PER_MANCHE; r++) {
      out.push(qualSessionType(m, r));
    }
  }
  return out;
})();

export function parseQualSessionType(type: string): { manche: number; race: number } | null {
  const match = /^qual_(\d)_(\d)$/.exec(type);
  if (!match) return null;
  const manche = Number(match[1]);
  const race = Number(match[2]);
  if (manche < 1 || manche > QUAL_MANCHES || race < 1 || race > QUAL_RACES_PER_MANCHE) {
    return null;
  }
  return { manche, race };
}

/** Manche qualif (1–4) depuis `qual_N_R` ou ancien format `qual_N`. */
export function qualMancheFromSessionType(type: string): number | null {
  const parsed = parseQualSessionType(type);
  if (parsed) return parsed.manche;
  const legacy = /^qual_([1-4])$/.exec(type);
  return legacy ? Number(legacy[1]) : null;
}

export function isQualSessionType(type: string): type is QualSessionType {
  return parseQualSessionType(type) !== null;
}

export function isQualTiebreakSessionType(type: string): type is QualTiebreakSessionType {
  return /^qual_tb_\d+$/.test(type);
}

/** Course qualif classique (hors barrage). */
export function isRegularQualSessionType(type: string): boolean {
  return isQualSessionType(type) || /^qual_[1-4]$/.test(type);
}

export function qualTiebreakSessionType(index: number): QualTiebreakSessionType {
  return `qual_tb_${index}`;
}

export function qualTiebreakIndexFromSessionType(type: string): number | null {
  const match = /^qual_tb_(\d+)$/.exec(type);
  return match ? Number(match[1]) : null;
}

/** Vrai pour une manche de qualification (y compris barrage) — jamais demi/finale.
 * Le plateau de jeu (tour de chauffe) ne précède que ces sessions-là. */
export function isQualificationSessionType(type: string): boolean {
  return isRegularQualSessionType(type) || isQualTiebreakSessionType(type);
}

/** Le plateau (tour de chauffe) ne précède que les manches de qualification,
 * et une seule fois par (meeting, division) — voir
 * `hasBoardPlayedForMeetingDivision` côté serveur pour `alreadyPlayed`. */
export function isBoardWarmupDue(type: string, alreadyPlayed: boolean): boolean {
  return isQualificationSessionType(type) && !alreadyPlayed;
}

export function sessionTypeLabel(type: MeetingSessionType): string {
  const parsed = parseQualSessionType(type);
  if (parsed) {
    return `Qualification — manche ${parsed.manche}, course ${parsed.race}`;
  }
  const tieIdx = qualTiebreakIndexFromSessionType(type);
  if (tieIdx != null) {
    return `Barrage qualification — groupe ${tieIdx}`;
  }
  switch (type) {
    case 'semi_1':
      return 'Demi-finale A';
    case 'semi_2':
      return 'Demi-finale B';
    case 'final':
      return 'Finale';
    default:
      return type;
  }
}

export function lapsForSessionType(type: MeetingSessionType): number {
  if (isQualTiebreakSessionType(type)) return QUAL_TIE_BREAK_LAPS;
  return type === 'final' || type.startsWith('semi') ? SEMI_FINAL_LAPS : QUAL_LAPS;
}
